import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Card, Modal, OverlayTrigger, Popover, Spinner, Table, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { parseCsvFile, vaildFileExtension } from "../../../../../utils/files";
import { ParseResult } from "papaparse";
import _ from "lodash";
import { toastr } from "react-redux-toastr";
import {
  hideUploadStudentDemographicModal,
  openFileUploadErrorModal,
  uploadStudentDemographics,
} from "../../../../../store/onboarding/actions";
import { ApplicationState } from "../../../../../store";
import RaceDemographicsExample from "../upload-student-data/RaceDemographicsExample";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import { faExclamationTriangle, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import AccordianContainer from "../../../../pages/coach-dashboard/tabs/your-assigments/common/AccordianContainer";
import { getMasterDataDisplayNames, getMasterDataVariations } from "../../../../../store/superuser/actions";
import { Master_Data, SuperuserState } from "../../../../../store/superuser/types";
import LoadingIndicator from "../../LoadingIndicator";
import { PopupDismissalKeys } from "../../../../../store/onboarding/types";
import { isShowConfimationDialog } from "../../../../../utils/NamesUtils";
import DemographicAdditionalContent from "../DemographicAdditionalContent";

type OwnProps = {};

type Props = OwnProps;

const COLUMN_NAMES = ["student_id", "Race"];

const UploadStudentDemographicModal: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const [openId, setOpenId] = useState<boolean>();
  const [raceVariation, setRaceVariation] = useState<any>([]);
 
  const show = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.modalsState.showUploadStudentDemographicModal
  );
  const isLoading = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.isLoading.uploadStudentDemographics
  );
  
  useEffect(() => {
    setOpenId(false);
    if(show) {
      dispatch<any>(getMasterDataDisplayNames('races'))
      .then((displayNameResponse: Master_Data[]) => {
        const raceVariations: { displayName: string | undefined; variations: string }[] = [];
        if(displayNameResponse.length) {
          for (let i = 0; i < displayNameResponse.length; i++) {
            const races = displayNameResponse[i];
            dispatch<any>(getMasterDataVariations(races.target_value!))
            .then((raceTypes: any) => {
              if(raceTypes.length) {
                const raceVariationsData = raceTypes.map((raceType: any) => raceType.variation).join('; ')
                raceVariations.push({
                  displayName: races.display_name,
                  variations: raceVariationsData
                })
                setRaceVariation(raceVariations);
              }
            });
          }
        }
      })
    }
  },[show])

  const handleHide = () => {
    dispatch(hideUploadStudentDemographicModal());
  };

  const fileInputRef = useRef<HTMLInputElement>(null);


  const isValidFileUploaded=(file: File)=>{
    const validExtensions = [
      'csv',
      'comma-separated-values',
      'vnd.ms-excel', 
      'vnd.msexcel',
      'xlsx', 
      'xls',
      'wps-office.xlsx',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const fileExtension = file.type.split('/')[1];
    return validExtensions.includes(fileExtension);
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files != null) {
      const file: File = event.target.files[0];
      if(!vaildFileExtension(file)) {
        return;
      }
      if(file != null && !isValidFileUploaded(file)){
        toastr.error('Error', 'This file format is not supported.')
        return;
      }
      parseCsvFile({
        file: file,
        onComplete: () => handleCSVData(file),
        onShowError: showError,
      });
      // clear the existing value
      event.target.value = "";
    }
  };

  const handleCSVData = (file: File) => {
    dispatch<any>(uploadStudentDemographics(file)).then(
      (updatedRecords: number) => {
        toastr.success(
          "Success",
          updatedRecords > 0 ? `Demographics are updated successfully for ${updatedRecords} student${updatedRecords > 1 ? '(s).' : ''}` 
          : ''+updatedRecords
        );
        handleHide();
      },
      (error: string | any[]) => {
        if (JSON.stringify(error)?.includes('tab_name')) {
          dispatch(openFileUploadErrorModal(error));
        } else {
          let title = 'Failed';
          if(error.includes('All demographic data in the file is already present and up to date.')) {
            title = 'Alert';
          }
          const errorMsg = Array.isArray(error)
            ? error.join(", ")
            : error;
          toastr.error(title, errorMsg);
        }
        
      }
    );
  };

  const showError = () => {
    toastr.error("Unable to upload a file:", "This file type is not supported");
  };
  const userDismissPopupDetails =
      useSelector(
        (s: ApplicationState) => s.auth.userInfo?.user_dismiss_pop_up_details || {}
      );
  const handleFileInputModal = () => {
    if (isShowConfimationDialog(userDismissPopupDetails[PopupDismissalKeys.UPLOAD_STUDENT_DEMOGRAPHICS])) {
      dispatch(showConfirmDialog({
        onConfirm: () => fileInputRef.current?.click(),
        centered: true,
        title: "Review Before Uploading",
        timerElipseLeft: true,
        countDownTimer: 5,
        isCustomisedContent: true,
        text: (
          <>
            <p className="text-center">Review your file using the guidance outlined on the page before uploading.</p>
            <p className="font-weight-bold mb-1 ml-4">Quick checks:</p>
            <ul>
              <li>Column names match exactly</li>
              <li>Casing is consistent</li>
              <li>No extra spaces</li>
            </ul>
            <p className="text-center">
              <FontAwesomeIcon 
                icon={faExclamationTriangle}
                style={{color:'red'}}
              /> {" "}
              <i>If the file doesn’t match, the upload may fail or be incomplete.</i>
            </p>
          </>
        ),
        confirmButtonText: "Upload File",
        popupKey: PopupDismissalKeys.UPLOAD_STUDENT_DEMOGRAPHICS
      }))
    } else {
      fileInputRef.current?.click()
    }
  }

  const localFileDownload = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.href = "https://readycoach-bucket.s3.us-east-2.amazonaws.com/readycoach-bucket/system/sample_files/sample_demographics.csv";
  }

  return (
    <Modal
      show={show}
      onHide={handleHide}
      animation={false}
      size="lg"
      backdropClassName="customDarkModalBackdrop in"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="purpleModalHeader">
        <Modal.Title>Upload Demographics</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2 className="text-center font-weight-semibold mb-xl-3 mb-2">
          We can guide you in uploading data.
        </h2>
        <h4 className="text-center mb-2">
          To upload demographic data from a file, it needs to be in a {" "}
          <a href="#" 
            onClick={localFileDownload} 
            className="link-offset-2 font-weight-bold cursor-pointer"
            >CSV or XLSX format</a>  {" "}
          and include a header row. It must include at least two columns; Student ID and at least one demographic detail.
        </h4>
        <h4 className="text-center mb-2">
          Columns in the file must match the names shown in the example below. For demographic fields (except Race and Gender), enter Yes/No or Y/N (case-insensitive). Leave the cell empty if the demographic does not apply.
        </h4>
        <h4 className="text-center mb-2"> To view the accepted Race/Ethnicity and Gender name variations, 
          please refer to the Race/Ethnicity and Gender Demographics section below.
        </h4>
        <div className="overflow-auto">
          <h4 className="font-weight-bold mb-2">Example Format</h4>
          <Table className="uploadExampleTable">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Race</th>
                <th>Special Education</th>
                <th>ELL</th>
                <th>SLD</th>
                <th>Gender</th>
                <th>Free or Reduced Lunch</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1001</td>
                <td>White</td>
                <td>Y <DemographicAdditionalContent isValue field="specialEducation"/></td>
                <td><DemographicAdditionalContent isValue field="ell"/></td>
                <td>n <DemographicAdditionalContent isValue field="sld"/></td>
                <td>Male <DemographicAdditionalContent isValue field="gender"/></td>
                <td>Yes <DemographicAdditionalContent isValue field="lunch"/></td>
              </tr>
            </tbody>
          </Table>
        </div>
        <p className="mb-1">In addition to matching the example format, please ensure your file does <b><u>not</u></b> contain any of the following:</p>
        <ul>
          <li>Duplicate Student IDs</li>
          <li>Blank Student IDs</li>
          <li>Student IDs containing characters other than letters and numbers</li>
        </ul>
        <Card>
          <Card.Header>
            <FontAwesomeIcon icon="info-circle" /> Why do we need Student ID?
          </Card.Header>
          <Card.Body>
            <Card.Text className="smallerText">
              <>
                The Student ID should be unique for each student across in all
                files uploaded to ReadyCoach. This lets us match up student data
                without relying on their names.
              </>
            </Card.Text>
          </Card.Body>
        </Card>
        <AccordianContainer 
          className="demographicsAccordion my-3"
          label="Race/Ethnicity and Gender Demographics Variations"
          open={openId}
          onClick={() => setOpenId(openId => !openId)}
        >
          {openId && !raceVariation.length ? <LoadingIndicator/> : '' }
          {openId && raceVariation.length ? <RaceDemographicsExample raceVariations={raceVariation}/> : ''}
        </AccordianContainer>
        <h5 className="text-center font-weight-normal my-3">
          Click the button below to choose a student data CSV or XLSX file from your
          computer to upload.
        </h5>
        <div className="text-center">
          <button
            className="blueBtnXL"
            data-cy="upload-data-csv-file"
            onClick={handleFileInputModal}
          >
            UPLOAD DEMOGRAPHICS FILE{" "}
            {isLoading && <Spinner animation="border" size="sm" />}
          </button>
          <input
            type="file"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={".csv, text/csv, .xlsx"}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default UploadStudentDemographicModal;
